import { ClientSide } from "~/@types";
import { IImageFields } from "~/@types/generated/contentful";
import { getFrontifyImageURL } from "./getFrontifyImage";

export interface ImageTransformations {
  quality?: number;
  width?: number;
  height?: number;
  format?: string;
  fit?: string;
}

const mapTransformationsToLongForm = (
  transformations: ImageTransformations,
): string => {
  const params: string[] = [];

  if (transformations.quality !== undefined) {
    params.push(`quality=${transformations.quality}`);
  }
  if (transformations.width !== undefined) {
    params.push(`width=${transformations.width}`);
  }
  if (transformations.height !== undefined) {
    params.push(`height=${transformations.height}`);
  }
  if (transformations.format) {
    params.push(`format=${transformations.format}`);
  }
  if (transformations.fit) {
    params.push(`fit=${transformations.fit}`);
  }

  return params.length > 0 ? `&${params.join("&")}` : "";
};

const mapTransformationsToShortForm = (
  transformations: ImageTransformations,
): string => {
  const params: string[] = [];

  if (transformations.quality !== undefined) {
    params.push(`q=${transformations.quality}`);
  }
  if (transformations.width !== undefined) {
    params.push(`w=${transformations.width}`);
  }
  if (transformations.height !== undefined) {
    params.push(`h=${transformations.height}`);
  }
  if (transformations.format) {
    params.push(`fm=${transformations.format}`);
  }
  if (transformations.fit) {
    params.push(`fit=${transformations.fit}`);
  }

  return params.length > 0 ? `&${params.join("&")}` : "";
};

const mapTransformationsToTwicAPI = (
  transformations: ImageTransformations,
): string => {
  const params: string[] = [];

  if (
    transformations.width !== undefined ||
    transformations.height !== undefined
  ) {
    params.push(
      `resize=${transformations.width ?? "-"}x${transformations.height ?? "-"}`,
    );
  }

  if (transformations.quality !== undefined) {
    params.push(`quality=${transformations.quality}`);
  }
  const format =
    transformations.format === "jpg" ? "jpeg" : transformations.format;

  params.push(`output=${format ?? "webp"}`);
  return `twic=v1/${params.join("/")}`;
};

export const parseTransformationString = (
  transformations: string,
): ImageTransformations => {
  const params: ImageTransformations = {};
  const parts = transformations.split("&").filter((p) => p);

  parts.forEach((part) => {
    const [key, value] = part.split("=");
    switch (key) {
      case "q":
      case "quality":
        params.quality = parseInt(value, 10);
        break;
      case "w":
      case "width":
        params.width = parseInt(value, 10);
        break;
      case "h":
      case "height":
        params.height = parseInt(value, 10);
        break;
      case "fm":
      case "format":
        params.format = value;
        break;
      case "fit":
        params.fit = value;
        break;
    }
  });

  return params;
};

export const getTwicPicsURL = (
  image: ClientSide<IImageFields>,
  transformations: string | ImageTransformations = "",
) => {
  const TWICPICS_DOMAIN = "https://tbi-assets.twic.pics";
  const ffySplitter = "frontify-enterprise-files-eu";
  const ctfSplitter = "75ila1cntaeh";

  const imageUrl = image?.frontifyImage
    ? getFrontifyImageURL(image)
    : image?.image?.fields?.file?.url;

  const transformObj =
    typeof transformations === "string"
      ? parseTransformationString(transformations)
      : transformations;

  if (imageUrl?.endsWith(".svg")) {
    if (image?.frontifyImage && !imageUrl.includes(ffySplitter)) {
      return imageUrl;
    }
    return image?.frontifyImage
      ? `${TWICPICS_DOMAIN}/ffy${imageUrl?.split(ffySplitter)[1]}`
      : `${TWICPICS_DOMAIN}/ctf${imageUrl?.split(ctfSplitter)[1]}`;
  }

  if (
    image?.frontifyImage &&
    imageUrl?.startsWith("https://media.ffycdn.net") &&
    !imageUrl?.includes(ffySplitter)
  ) {
    const longFormParams = mapTransformationsToLongForm({
      format: "webp",
      ...transformObj,
      width: transformObj.width ?? 1920,
    });
    return `${imageUrl}?${longFormParams}`;
  }

  if (image?.frontifyImage && imageUrl?.split(ffySplitter)[1] !== undefined) {
    const hasTransformations = Object.values(transformObj).some(
      (value) => value !== undefined,
    );
    return `${TWICPICS_DOMAIN}/ffy${imageUrl?.split(ffySplitter)[1]}?${
      hasTransformations ? mapTransformationsToTwicAPI(transformObj) : "fm=webp"
    }`;
  }

  if (!image?.frontifyImage && imageUrl?.split(ctfSplitter)[1] !== undefined) {
    const shortFormParams = mapTransformationsToShortForm(transformObj);
    return `${TWICPICS_DOMAIN}/ctf${
      imageUrl?.split(ctfSplitter)[1]
    }?fm=webp${shortFormParams}`;
  }

  return imageUrl || "";
};

export const getTwicPicsURLAsset = (url: string) => {
  const TWICPICS_DOMAIN = "https://tbi-assets.twic.pics";
  const ctfSplitter = "75ila1cntaeh";

  return `${TWICPICS_DOMAIN}/ctf${url.split(ctfSplitter)[1]}?fm=webp`;
};

export const getTwicPicsURLFromSrc = (
  url: string,
  transformations: string | ImageTransformations = "",
) => {
  const TWICPICS_DOMAIN = "https://tbi-assets.twic.pics";
  const ctfSplitter = "75ila1cntaeh";

  const transformObj =
    typeof transformations === "string"
      ? parseTransformationString(transformations)
      : transformations;

  const shortFormParams = mapTransformationsToShortForm(transformObj);

  return `${TWICPICS_DOMAIN}/ctf${
    url.split(ctfSplitter)[1]
  }?fm=webp${shortFormParams}`;
};
